import React from 'react';
import {
  AlertTriangle,
  Database,
  Wifi,
  MapPin,
  RefreshCw,
  Settings,
  FileText,
  ExternalLink,
  Home,
  HelpCircle,
  AlertCircle,
  XCircle
} from 'lucide-react';

const ErrorScreen = ({
  type = "general", // "no-data", "network", "validation", "map", "api", "general"
  title,
  message,
  details,
  onRetry,
  onGoHome,
  showContactInfo = true,
  customActions = []
}) => {

  // Error type configurations
  const getErrorConfig = () => {
    switch (type) {
      case "no-data":
        return {
          icon: Database,
          iconColor: "text-red-600 dark:text-red-400",
          bgColor: "from-red-50 to-red-100/50",
          borderColor: "border-red-300",
          title: title || "No Data Available",
          message: message || "API endpoint is not available.",
          suggestions: [
            "Contact Admin"
          ]
        };

      case "network":
        return {
          icon: Wifi,
          iconColor: "text-orange-600",
          bgColor: "from-orange-50 to-orange-100/50",
          borderColor: "border-orange-300",
          title: title || "Network Connection Error",
          message: message || "Unable to connect to data sources or external services.",
          suggestions: [
            "Check your internet connection",
            "Verify API endpoints are accessible",
            "Check firewall and proxy settings",
            "Try refreshing the page"
          ]
        };

      case "validation":
        return {
          icon: AlertCircle,
          iconColor: "text-yellow-600",
          bgColor: "from-yellow-50 to-yellow-100/50",
          borderColor: "border-yellow-300 dark:border-yellow-500/30",
          title: title || "Data Validation Error",
          message: message || "The loaded data contains invalid or corrupted information.",
          suggestions: [
            "Check the data format and required fields",
            "Verify latitude/longitude coordinates are valid",
            "Ensure date/time fields are properly formatted",
            "Remove any special characters or empty rows"
          ]
        };

      case "map":
        return {
          icon: MapPin,
          iconColor: "text-purple-600 dark:text-purple-300",
          bgColor: "from-purple-50 to-purple-100/50",
          borderColor: "border-purple-300",
          title: title || "Map Initialization Error",
          message: message || "Unable to initialize the interactive map component.",
          suggestions: [
            "Check Mapbox access token configuration",
            "Verify WebGL support in your browser",
            "Clear browser cache and reload",
            "Try using a different browser"
          ]
        };

      case "api":
        return {
          icon: Settings,
          iconColor: "text-indigo-600",
          bgColor: "from-indigo-50 to-indigo-100/50",
          borderColor: "border-indigo-300",
          title: title || "API Connection Error",
          message: message || "Unable to connect to the oceanographic data API.",
          suggestions: [
            "Verify API endpoint URL is correct",
            "Check API authentication credentials",
            "Confirm API server is running and accessible",
            "Review API rate limits and quotas"
          ]
        };

      default:
        return {
          icon: XCircle,
          iconColor: "text-blue-600 dark:text-blue-400",
          bgColor: "from-blue-50 to-blue-100/50",
          borderColor: "border-blue-300",
          title: title || "Application Error",
          message: message || "An unexpected error occurred while running the oceanographic platform.",
          suggestions: [
            "Try refreshing the browser page",
            "Clear browser cache and cookies",
            "Check browser console for error details",
            "Contact support if the issue persists"
          ]
        };
    }
  };

  const config = getErrorConfig();
  const IconComponent = config.icon;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white flex items-center justify-center px-4 dark:bg-slate-900 dark:text-white">
      <div className={`text-center max-w-2xl w-full bg-gradient-to-b ${config.bgColor} border ${config.borderColor} rounded-lg p-6 md:p-8 shadow-lg relative`}>

        {/* Error Icon */}
        <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-6 relative">
          <div className="absolute inset-0 bg-white dark:bg-slate-900 rounded-full flex items-center justify-center shadow-sm dark:bg-slate-800">
            <IconComponent className={`w-8 h-8 md:w-10 md:h-10 ${config.iconColor}`} />
          </div>
          <div className="absolute inset-0 border-2 border-slate-200 dark:border-slate-700 rounded-full animate-pulse dark:border-slate-600"></div>
        </div>

        {/* Title */}
        <h2 className="text-xl md:text-2xl font-bold text-slate-800 dark:text-white mb-4 dark:text-white">
          {config.title}
        </h2>

        {/* Main Message */}
        <p className="text-sm md:text-base text-slate-600 dark:text-slate-300 mb-6 leading-relaxed dark:text-slate-300">
          {config.message}
        </p>

        {/* Error Details (if provided) */}
        {details && (
          <div className="bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-lg p-4 mb-6 text-left dark:bg-slate-800/50 dark:border-slate-700">
            <h4 className="font-semibold text-slate-700 dark:text-slate-200 mb-2 flex items-center gap-2 dark:text-slate-200">
              <AlertTriangle className="w-4 h-4 text-amber-500 dark:text-yellow-400" />
              Error Details
            </h4>
            <div className="text-sm text-slate-600 dark:text-slate-300 font-mono bg-white dark:bg-slate-900 p-3 rounded border border-slate-200 dark:border-slate-700 overflow-x-auto dark:text-slate-400 dark:bg-slate-900/50">
              {typeof details === 'string' ? details : JSON.stringify(details, null, 2)}
            </div>
          </div>
        )}

        {/* Suggested Solutions */}
        <div className="bg-white/60 border border-slate-200 dark:border-slate-700 rounded-lg p-4 mb-6 text-left dark:bg-slate-800/30 dark:border-slate-700">
          <h4 className="font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2 dark:text-slate-200">
            <HelpCircle className="w-4 h-4 text-blue-500 dark:text-blue-400" />
            Suggested Solutions
          </h4>
          <ul className="space-y-2">
            {config.suggestions.map((suggestion, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300 dark:text-slate-300">
                <span className="text-blue-500 mt-1 dark:text-blue-400">•</span>
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {onRetry && (
            <button
              onClick={onRetry}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          )}

          {onGoHome && (
            <button
              onClick={onGoHome}
              className="flex items-center justify-center gap-2 bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-6 py-3 rounded-lg transition-colors font-medium dark:bg-slate-700 dark:hover:bg-slate-600"
            >
              <Home className="w-4 h-4" />
              Go Home
            </button>
          )}

          {/* Custom Actions */}
          {customActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg transition-colors font-medium ${action.className || 'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200'}`}
            >
              {action.icon && <action.icon className="w-4 h-4" />}
              {action.label}
            </button>
          ))}
        </div>

        {/* Contact Information */}
        {showContactInfo && (
          <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700 dark:border-slate-700">
            <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1 dark:text-slate-400">
              <div className="font-semibold text-slate-600 dark:text-slate-300 mb-2 dark:text-slate-300">Need Additional Help?</div>
              <div className="flex items-center justify-center gap-4 mt-3">
                <button
                  type="button"
                  className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-500 transition-colors dark:text-blue-400 dark:hover:text-blue-300 bg-transparent border-0 p-0 cursor-pointer font-inherit"
                >
                  <ExternalLink className="w-3 h-3" />
                  Documentation
                </button>
                <button
                  type="button"
                  className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-500 transition-colors dark:text-blue-400 dark:hover:text-blue-300 bg-transparent border-0 p-0 cursor-pointer font-inherit"
                >
                  <ExternalLink className="w-3 h-3" />
                  Support
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subtle Background Effect */}
        <div className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-red-500/5 rounded-full"></div>
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-blue-500/5 rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

// Pre-configured error screen variants
export const NoDataError = (props) => (
  <ErrorScreen type="no-data" {...props} />
);

export const NetworkError = (props) => (
  <ErrorScreen type="network" {...props} />
);

export const DataValidationError = (props) => (
  <ErrorScreen type="validation" {...props} />
);

export const MapError = (props) => (
  <ErrorScreen type="map" {...props} />
);

export const APIError = (props) => (
  <ErrorScreen type="api" {...props} />
);

// Example usage with custom actions
export const CustomActionError = (props) => {
  const customActions = [
    {
      label: "Download Sample Data",
      icon: FileText,
      onClick: () => console.log("Download sample data"),
      className: "bg-green-600 hover:bg-green-700"
    },
    {
      label: "Contact Support",
      icon: ExternalLink,
      onClick: () => window.open('mailto:support@bluemvmt.com'),
      className: "bg-blue-600 hover:bg-blue-700"
    }
  ];

  return (
    <ErrorScreen
      type="no-data"
      customActions={customActions}
      {...props}
    />
  );
};

export default ErrorScreen;